import {BrickContext} from 'olympe';
import {Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow} from '@mui/material';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {getPunchoutCatalogsInfoLambda, getPunchoutCatalogURLLambda} from '../../../../utils/functionSignatures';
import {LAMBDA_NAMES} from '../../../../utils/constants';
import {AccessCatalogueDialog} from '../../../dialogs/AccessCataloguePopup.jsx';
import {PaginationActions} from '../../../PaginationActions.jsx';
import {ErrorPopup} from '../ErrorPopup.jsx';

/**
 * @param {SuppliersTable.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function SuppliersTable(props) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [confirmPunchoutOpen, setConfirmPunchoutOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState({});
    const [punchoutCatalogs, setPunchoutCatalogs] = useState([]);
    const [fetchingURL, setFetchingURL] = useState(false);
    const [errorPopupOpen, setErrorPopupOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const getPunchoutCatalogs = props.context.get(LAMBDA_NAMES.GET_PUNCHOUT_CATALOGS);
    const getPunchoutCatalogURL = props.context.get(LAMBDA_NAMES.GET_PUNCHOUT_CATALOG_URL);

    // This useEffect is triggered when the table is displayed. It will fetch the info about the punchout catalogs.
    useEffect(() => {
        if (!props.punchoutCatalog) {
            return;
        }

        getPunchoutCatalogsInfoLambda(props.context, getPunchoutCatalogs, props.companyCode?.value)
            .then((r) => {
                const results = JSON.parse(r[0]);
                setPunchoutCatalogs(results.map((v) => ({
                    supplierCode: v['vendor_code'],
                    url: v['url'],
                    companyCode: v['company_code'],
                    purchaseOrg: v['purchase_org'],
                })));
            })
            .catch((error) => console.error('Error getPunchoutCatalogs', error));
    }, []);


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleClickOnPunchout = (supplierCode, supplierName, catalogUrl) => {
        const supplier = props.suppliers.find((s) => s['lifnr'] === supplierCode);
        const punchoutCatalogInfo = punchoutCatalogs.filter((v) =>
            (v.supplierCode === supplierCode) &&
            (v.companyCode === supplier['bukrs']) &&
            (v.purchaseOrg === supplier['ekorg']));
        if (punchoutCatalogInfo.length > 0) {
            setSelectedSupplier({
                code: supplierCode,
                name: supplierName,
                url: catalogUrl,
                companyCode: supplier['bukrs'],
            });
            setConfirmPunchoutOpen(true);
        } else {
            props.handleClick(supplierCode, supplierName);
        }
    };

    const handleCloseConfirmPunchoutPopup = () => {
        setConfirmPunchoutOpen(false);
    };

    const handleStayOnER = () => {
        props.handleClick(selectedSupplier.code, selectedSupplier.name);
    };

    const handleAccessCatalog = () => {
        setFetchingURL(true);
        getPunchoutCatalogURLLambda(props.context,
            getPunchoutCatalogURL,
            selectedSupplier.code,
            selectedSupplier.name,
            selectedSupplier.companyCode,
            props.companyCode.label,
            props.purchaseOrg.label,
            props.DYNInfo?.plant || props.PRInfo?.plant,
            props.DYNInfo?.deliveryAddress || props.PRInfo?.deliveryAddress,
            props.DYNInfo?.materialCode || props.PRInfo?.materialCode,
            props.DYNInfo?.l3Category || props.PRInfo?.l3Category,
            props.EPRId,
            `punchout`)
            .then((results) => {
                const url = results[0];
                window.open(url, '_blank').focus();
                setConfirmPunchoutOpen(false);
                props.onClosePunchOut(`${selectedSupplier.code} - ${selectedSupplier.name}`); // Close the parent popup
            })
            .catch((err) => {
                console.error('Failed to get PunchOut URL', err);
                setErrorMessage(`Failed to open catalog for supplier ${selectedSupplier.code} - ${selectedSupplier.name}. Please try again or contact support.`);
                setErrorPopupOpen(true);
                setConfirmPunchoutOpen(false);
            })
            .finally(() => setFetchingURL(false));
    };

    const columns = [
        {id: 'lifnr', label: 'Code', width: 100},
        {id: 'name1', label: 'Name', width: 180},
        {id: 'ort01', label: 'Region', width: 70},
        {id: 'land1', label: 'Country', width: 50},
        {id: 'ekorg', label: 'Purchase Org', width: 50},
        {id: 'bukrs', label: 'Company Code', width: 50},
        {id: 'ktokk', label: 'Account Group', width: 50},
        {id: 'stceg', label: 'VAT', width: 40},
    ];

    const filteredSuppliers = props.suppliers
        .map((supplier) => {
            let isDisabled = props.companyCode.value !== supplier['bukrs'] || props.purchaseOrg.value !== supplier['ekorg'] || props.disableAll;

            if (isDisabled && props.companyCode.value?.startsWith('DE') && props.purchaseOrg.value === supplier['ekorg']) {
                isDisabled = false;
            }

            return {
                ...supplier,
                isDisabled,
            };
        })
        .filter((supplier) => {
            return !(props.localizedOnly && supplier.isDisabled);
        });

    return (
        <>
            <div>
                <TableContainer
                    className='list-of-pr'
                    sx={{
                        borderRadius: '4px',
                        overflow: 'hidden',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                    }}
                >
                    <Table stickyHeader aria-label={'suppliers table'}>
                        <TableHead>
                            <TableRow className={'pr-editor-search-supplier-popup-menu-header'} sx={{
                                '& .MuiTableCell-root': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                            }}>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        style={{width: column.width, background: '#F7F7F7'}}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{
                            '& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root': {
                                backgroundColor: '#FAFAFA',
                            },
                        }}>
                            {filteredSuppliers
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((supplier) => {
                                    const hasPunchoutCatalog = punchoutCatalogs.some((v) => v.supplierCode === supplier['lifnr']);
                                    const punchoutCatalogInfo = hasPunchoutCatalog ? punchoutCatalogs.find((v) => v.supplierCode === supplier['lifnr']) : null;
                                    return (
                                        <TableRow
                                            key={`${supplier['lifnr']} ${supplier['ekorg'] || ''} ${supplier['bukrs'] || ''}`}
                                            hover={!supplier.isDisabled}
                                            className={supplier.isDisabled ? 'pr-editor-search-supplier-popup-table-row-disabled-background' : ''}
                                            sx={{cursor: supplier.isDisabled ? 'auto' : 'pointer'}}
                                            onClick={() => supplier.isDisabled ? undefined : hasPunchoutCatalog ? handleClickOnPunchout(supplier['lifnr'], supplier['name1'], punchoutCatalogInfo?.url) : props.handleClick(supplier['lifnr'], supplier['name1'])}
                                        >
                                            {columns.map((column) => {
                                                const value = supplier[column.id];
                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        className={`pr-editor-search-supplier-popup-table-row${supplier.isDisabled ? '-disabled' : ''}-text`}
                                                    >
                                                        {value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[]}
                    component="div"
                    count={props.count}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={PaginationActions}
                    labelDisplayedRows={() => ''}
                    sx={{
                        '& .MuiTablePagination-toolbar': {
                            justifyContent: 'flex-end',
                            px: '24px',
                            py: '8px',
                        },
                    }}
                />
            </div>
            <AccessCatalogueDialog
                open={confirmPunchoutOpen}
                onClose={handleCloseConfirmPunchoutPopup}
                onSkip={handleStayOnER}
                onAccess={handleAccessCatalog}
                loading={fetchingURL}
                headline="A catalog of items has been negotiated with this supplier"
                body="Would you like to access their catalog?"
            />
            <ErrorPopup
                open={errorPopupOpen}
                onClose={() => setErrorPopupOpen(false)}
                errorMessages={[errorMessage]}
                title="PunchOut Catalog Access Error"
            />
        </>
    );
}

SuppliersTable.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    suppliers: PropTypes.arrayOf(PropTypes.object),
    handleClick: PropTypes.func,
    companyCode: PropTypes.object,
    purchaseOrg: PropTypes.object,
    localizedOnly: PropTypes.bool,
    count: PropTypes.number,
    disableAll: PropTypes.bool,
    punchoutCatalog: PropTypes.bool,
    DYNInfo: PropTypes.object,
    PRInfo: PropTypes.object,
    EPRId: PropTypes.string,
    onClosePunchOut: PropTypes.func,
};

SuppliersTable.defaultProps = {
    disableAll: false,
};
